<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import VuePdfEmbed from 'vue-pdf-embed';
import { cn } from '@/helpers/tailwind-helpers';
import BaseLabel from '@/atoms/BaseLabel.vue';
import PageNavigation from '@/atoms/PageNavigation.vue';
import ZoomController from '@/atoms/ZoomController.vue';
import BaseButton from '@/atoms/BaseButton.vue';
import { useFileOpener } from '@/composables/useFileOpener';
import { useZoom } from '@/composables/useZoom';
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from '@/constant/files';

export type GenericFileViewProps = {
  src: string;
  fileType: 'pdf' | 'image';
  placeholderText?: string;
  class?: string;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  pdfNavigation?: 'buttons' | 'scroll';
  showZoomController?: boolean;
};

const props = withDefaults(defineProps<GenericFileViewProps>(), {
  placeholderText: 'Fichier non disponible',
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  zoomStep: ZOOM_STEP,
  pdfNavigation: 'scroll',
  showZoomController: true,
});

const emit = defineEmits<{
  error: [error: Event | string];
  loaded: [];
  pagesVisited: [data: { pages: number; percentage: number }];
}>();

/* =========================
   STATE
========================= */
const hasError = ref(false);
const hasPdfError = ref(false);
const pdfLoading = ref(true);
const pdfProgress = ref(0);
const pdfPageCount = ref(0);
const currentPage = ref(1);

const maxScrollPercentage = ref(0);
const maxPageReached = ref(1);

const { openFile } = useFileOpener();

const zoomContainerRef = ref<HTMLElement>();
const contentInnerRef = ref<HTMLElement>();
const { zoom, ty, isPinching, contentStyle } = useZoom(zoomContainerRef, {
  minZoom: props.minZoom,
  maxZoom: props.maxZoom,
});

/* =========================
   COMPUTED
========================= */
const isPdf = computed(() => props.fileType === 'pdf');
const isButtonNavigation = computed(() => props.pdfNavigation === 'buttons');

const containerClass = computed(() =>
  cn(
    'relative flex flex-col bg-Colors-Background-bg-secondary rounded-2xl overflow-hidden',
    props.class,
  ),
);

/* =========================
   RESET SI NOUVEAU PDF
========================= */
watch(
  () => props.src,
  () => {
    pdfLoading.value = true;
    pdfProgress.value = 0;
    hasPdfError.value = false;
    currentPage.value = 1;
    maxScrollPercentage.value = 0;
    maxPageReached.value = 1;
  },
);

/* =========================
   SCROLL TRACKING (via pan Y)
========================= */
watch(ty, () => {
  if (props.pdfNavigation !== 'scroll') return;
  if (!zoomContainerRef.value || !contentInnerRef.value || pdfPageCount.value === 0) return;

  const containerH = zoomContainerRef.value.clientHeight;
  const contentH = contentInnerRef.value.offsetHeight;
  const maxScroll = Math.max(0, contentH - containerH);
  if (maxScroll === 0) return;

  const currentScroll = Math.abs(Math.min(0, ty.value));
  const percentage = Math.min(1, currentScroll / maxScroll);

  if (percentage > maxScrollPercentage.value) {
    maxScrollPercentage.value = percentage;
    const calculatedPage = Math.ceil(percentage * pdfPageCount.value);
    if (calculatedPage > maxPageReached.value) {
      maxPageReached.value = calculatedPage;
      emit('pagesVisited', {
        pages: maxPageReached.value,
        percentage: Math.round(percentage * 100),
      });
    }
  }
});

/* =========================
   HANDLERS
========================= */
const handleOpenFile = () => {
  openFile(props.src);
};

const goToPrevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const goToNextPage = () => {
  if (currentPage.value < pdfPageCount.value) currentPage.value++;
};

const handlePdfProgress = (progressData: { loaded: number; total: number }) => {
  if (!progressData.total) return;
  pdfProgress.value = Math.round((progressData.loaded / progressData.total) * 100);
};

const handlePdfLoaded = (pdfDocument: { numPages: number }) => {
  pdfLoading.value = false;
  pdfPageCount.value = pdfDocument.numPages;
  pdfProgress.value = 100;
  emit('loaded');
};

const handlePdfError = (error: Error) => {
  pdfLoading.value = false;
  hasPdfError.value = true;
  emit('error', error.message || 'Failed to load PDF');
};

const handleError = (event: Event) => {
  hasError.value = true;
  emit('error', event);
};
</script>

<template>
  <article :class="containerClass">
    <!-- ERROR -->
    <section v-if="hasError || !src" class="flex-1 flex items-center justify-center min-h-[200px]">
      <BaseLabel :text="placeholderText" color="tertiary" preset="small" />
    </section>

    <!-- PDF -->
    <template v-else-if="isPdf">
      <section class="flex-1 flex flex-col min-h-[200px]">
        <!-- LOADING -->
        <div
          v-if="pdfLoading && !hasPdfError"
          class="flex-1 flex flex-col items-center justify-center gap-4 min-h-[200px] px-6"
        >
          <BaseLabel
            :text="`Chargement du PDF... ${pdfProgress}%`"
            color="tertiary"
            preset="small"
          />
          <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              :style="{ width: pdfProgress + '%' }"
              class="h-full bg-primary transition-all duration-300 ease-out"
            />
          </div>
        </div>

        <!-- ERROR -->
        <div
          v-else-if="hasPdfError"
          class="flex-1 flex flex-col items-center justify-center gap-4 min-h-[200px] p-4"
        >
          <BaseLabel color="tertiary" preset="small" text="Impossible d'afficher le PDF" />
          <BaseButton label="Ouvrir le fichier" @click="handleOpenFile" />
        </div>

        <!-- CONTENT -->
        <div
          v-show="!pdfLoading && !hasPdfError"
          ref="zoomContainerRef"
          class="flex-1 overflow-hidden"
        >
          <div ref="contentInnerRef" :style="contentStyle" class="p-4">
            <VuePdfEmbed
              :page="isButtonNavigation ? currentPage : undefined"
              :source="src"
              @loaded="handlePdfLoaded"
              @progress="handlePdfProgress"
              @loading-failed="handlePdfError"
              @rendering-failed="handlePdfError"
            />
          </div>
        </div>

        <!-- CONTROLS -->
        <div
          v-if="!hasPdfError"
          class="flex items-center justify-center gap-2 py-3 border-t border-primary"
        >
          <ZoomController
            v-if="showZoomController"
            v-model:zoom="zoom"
            :max-zoom="maxZoom"
            :min-zoom="minZoom"
            :step="zoomStep"
          />

          <PageNavigation
            v-if="isButtonNavigation"
            :current-page="currentPage"
            :total-pages="pdfPageCount"
            @next="goToNextPage"
            @prev="goToPrevPage"
          />
        </div>
      </section>
    </template>

    <!-- IMAGE -->
    <template v-else>
      <section
        ref="zoomContainerRef"
        class="flex-1 overflow-hidden"
      >
        <div :style="contentStyle" class="flex items-center justify-center min-h-[200px] p-4">
          <ion-img
            :alt="src"
            :src="src"
            class="max-w-full h-auto object-contain"
            @ionError="handleError"
          />
        </div>
      </section>

      <div class="flex items-center justify-center gap-2 py-3 border-t border-primary">
        <ZoomController
          v-if="showZoomController"
          v-model:zoom="zoom"
          :max-zoom="maxZoom"
          :min-zoom="minZoom"
          :step="zoomStep"
        />
      </div>
    </template>

    <div v-if="$slots.default" class="p-4 border-t border-primary">
      <slot />
    </div>
  </article>
</template>
